import * as React from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar as CalendarIcon, ChevronDown } from "lucide-react";
import { DateRange } from "react-day-picker";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";

interface DateRangePickerProps extends React.HTMLAttributes<HTMLDivElement> {
  date: DateRange | undefined;
  setDate: (date: DateRange | undefined) => void;
  className?: string;
}

export function DateRangePicker({
  date,
  setDate,
  className,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "w-full justify-start text-left font-normal h-10 px-3 transition-all hover:bg-muted border-border shadow-sm",
              !date && "text-muted-foreground"
            )}
          >
            <div className="flex items-center gap-2 w-full">
                <div className="bg-muted p-1 rounded-md text-muted-foreground">
                    <CalendarIcon className="h-4 w-4" />
                </div>
                <div className="flex flex-col items-start leading-tight flex-1 overflow-hidden">
                    <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Período de consulta</span>
                    <span className="text-sm font-medium truncate w-full">
                        {date?.from ? (
                            date.to ? (
                                <>
                                {format(date.from, "dd 'de' MMM, yyyy", { locale: ptBR })} -{" "}
                                {format(date.to, "dd 'de' MMM, yyyy", { locale: ptBR })}
                                </>
                            ) : (
                                format(date.from, "dd 'de' MMM, yyyy", { locale: ptBR })
                            )
                        ) : (
                            <span>Selecione uma data</span>
                        )}
                    </span>
                </div>
                <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform duration-200", isOpen && "rotate-180")} />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 shadow-2xl border-border rounded-xl overflow-hidden" align="end">
          <div className="p-2">
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={date?.from}
              selected={date}
              onSelect={setDate}
              numberOfMonths={2}
              locale={ptBR}
              className="rounded-md"
            />
            <Separator className="my-2" />
            <div className="flex items-center justify-between px-2 pb-2">
                <div className="flex flex-col">
                    <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Início</span>
                    <span className="text-xs font-semibold">{date?.from ? format(date.from, "dd/MM/yyyy") : "-"}</span>
                </div>
                <div className="h-4 w-px bg-border mx-4" />
                <div className="flex flex-col items-end">
                    <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Fim</span>
                    <span className="text-xs font-semibold">{date?.to ? format(date.to, "dd/MM/yyyy") : "-"}</span>
                </div>
                <div className="flex-1" />
                <Button 
                    size="sm" 
                    className="ml-4 h-8 px-4" 
                    onClick={() => setIsOpen(false)}
                >
                    Aplicar
                </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
